import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    // Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    Stack,
    Avatar,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Search as SearchIcon,
    // FilterList as FilterIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";
import { SeverityPill } from "@/components/severity-pill";

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    });
    const [filters, setFilters] = useState({
        search: "",
        status: "",
        authMethod: "",
    });

    // Fetch users from API
    const fetchUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                ...(filters.search && { search: filters.search }),
                ...(filters.status && { status: filters.status }),
                ...(filters.authMethod && { authMethod: filters.authMethod }),
            });

            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/users?${params}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch users");
            }

            const data = await response.json();
            setUsers(data.users);
            setPagination({
                page: data.page,
                limit: data.limit,
                total: data.total,
                totalPages: data.totalPages,
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, pagination.limit, filters]);

    const handleSearchChange = (event) => {
        setFilters(prev => ({ ...prev, search: event.target.value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handleStatusChange = (event) => {
        setFilters(prev => ({ ...prev, status: event.target.value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleAuthMethodChange = (event) => {
        setFilters(prev => ({ ...prev, authMethod: event.target.value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (event, value) => {
        setPagination(prev => ({ ...prev, page: value }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "active":
                return "success";
            case "pending":
                return "warning";
            case "suspended":
                return "error";
            case "inactive":
                return "secondary";
            default:
                return "default";
        }
    };

    const getAuthMethodColor = (authMethod) => {
        switch (authMethod) {
            case "google":
                return "primary";
            case "facebook":
                return "info";
            case "apple":
                return "secondary";
            case "email":
                return "success";
            default:
                return "default";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getInitials = (name) => {
        if (!name) return "U";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                py: 8,
            }}
        >
            <Container maxWidth="xl">
                <Stack spacing={3}>
                    {/* Header */}
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            User Management
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage and view all registered users
                        </Typography>
                    </Box>

                    {/* Filters */}
                    <Card sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                placeholder="Search users..."
                                value={filters.search}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                                }}
                                sx={{ minWidth: 300 }}
                            />

                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    label="Status"
                                    onChange={handleStatusChange}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Auth Method</InputLabel>
                                <Select
                                    value={filters.authMethod}
                                    label="Auth Method"
                                    onChange={handleAuthMethodChange}
                                >
                                    <MenuItem value="">All Methods</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="google">Google</MenuItem>
                                    <MenuItem value="facebook">Facebook</MenuItem>
                                    <MenuItem value="apple">Apple</MenuItem>
                                </Select>
                            </FormControl>

                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchUsers} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Card>

                    {/* Users Table */}
                    <Card>
                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Auth Method</TableCell>
                                        <TableCell>Verified</TableCell>
                                        <TableCell>Onboarding</TableCell>
                                        <TableCell>User Type</TableCell>
                                        <TableCell>Created At</TableCell>
                                        <TableCell>Last Login</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Typography>Loading...</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : error ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Typography color="error">{error}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : users.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Typography>No users found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        users.map((user) => (
                                            <TableRow key={user._id} hover>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Avatar sx={{ bgcolor: "primary.main" }}>
                                                            {getInitials(user.name || user.firstName)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2">
                                                                {user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A"}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                ID: {user._id}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{user.email}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <SeverityPill color={getStatusColor(user.status)}>
                                                        {user.status || "N/A"}
                                                    </SeverityPill>
                                                </TableCell>
                                                <TableCell>
                                                    <SeverityPill color={getAuthMethodColor(user.authMethod)}>
                                                        {user.authMethod || "N/A"}
                                                    </SeverityPill>
                                                </TableCell>
                                                <TableCell>
                                                    <SeverityPill color={user.verified ? "success" : "warning"}>
                                                        {user.verified ? "Verified" : "Unverified"}
                                                    </SeverityPill>
                                                </TableCell>
                                                <TableCell>
                                                    <SeverityPill color={user.onboardingCompleted ? "success" : "warning"}>
                                                        {user.onboardingCompleted ? "Completed" : "Pending"}
                                                    </SeverityPill>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {user.userType || "N/A"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(user.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(user.lastLoginAt)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                                <Pagination
                                    count={pagination.totalPages}
                                    page={pagination.page}
                                    onChange={handlePageChange}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </Card>

                    {/* Summary */}
                    <Card sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {users.length} of {pagination.total} users
                        </Typography>
                    </Card>
                </Stack>
            </Container>
        </Box>
    );
};

export default UsersPage;


